// client/src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Card, 
  PageTitle, 
  Spinner, 
  EmptyState 
} from '../components';
import { 
  FaDesktop, 
  FaExclamationCircle, 
  FaHistory, 
  FaBell, 
  FaPlus, 
  FaExclamationTriangle 
} from 'react-icons/fa';
import ManualCheckButton from '../components/ManualCheckButton';
import siteService from '../services/siteService';
import changeService from '../services/changeService';
import { formatRelativeTime, formatImportance } from '../utils/formatters';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalSites: 0,
    totalChanges: 0,
    unreadChanges: 0,
    recentChanges: []
  });
  const [sites, setSites] = useState([]);

  // 获取仪表盘数据
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // 并行获取站点和变更数据
        const [sitesData, changesStats, recentChanges] = await Promise.all([
          siteService.getSites(),
          changeService.getUnreadStats(),
          changeService.getChanges({ limit: 5, sort: '-createdAt' })
        ]);
        
        setSites(sitesData);
        setStats({
          totalSites: sitesData.length,
          totalChanges: changesStats.total || 0,
          unreadChanges: changesStats.unread || 0,
          recentChanges: recentChanges.changes || []
        });
      } catch (err) {
        console.error('获取仪表盘数据失败', err);
        setError('获取数据失败，请刷新页面重试');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  // 如果正在加载，显示加载中状态
  if (loading) {
    return <Spinner fullPage text="加载仪表盘数据..." />;
  }

  return (
    <div className="dashboard-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <PageTitle 
          title={`欢迎回来，${currentUser?.name || '用户'}`}
          subtitle="查看您的网站监控概览"
        />
        <div>
          <ManualCheckButton />
        </div>
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          <FaExclamationCircle className="me-2" />
          {error}
        </div>
      )}
      
      {/* 统计卡片 */}
      <div className="row mb-4">
        {/* 监控站点数 */}
        <div className="col-md-4 mb-4 mb-md-0">
          <Card className="h-100">
            <div className="d-flex align-items-center">
              <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                <FaDesktop className="text-primary" size={24} />
              </div>
              <div className="ms-3">
                <h4 className="mb-0">{stats.totalSites}</h4>
                <p className="text-muted mb-0">监控站点</p>
              </div>
              <Link to="/sites" className="ms-auto btn btn-sm btn-outline-primary">
                管理站点
              </Link>
            </div>
          </Card>
        </div>
        
        {/* 变更总数 */}
        <div className="col-md-4 mb-4 mb-md-0">
          <Card className="h-100">
            <div className="d-flex align-items-center">
              <div className="rounded-circle bg-success bg-opacity-10 p-3">
                <FaHistory className="text-success" size={24} />
              </div>
              <div className="ms-3">
                <h4 className="mb-0">{stats.totalChanges}</h4>
                <p className="text-muted mb-0">变更总数</p>
              </div>
              <Link to="/changes" className="ms-auto btn btn-sm btn-outline-success">
                查看变更
              </Link>
            </div>
          </Card>
        </div>
        
        {/* 未读变更 */}
        <div className="col-md-4">
          <Card className="h-100">
            <div className="d-flex align-items-center">
              <div className="rounded-circle bg-danger bg-opacity-10 p-3">
                <FaBell className="text-danger" size={24} />
              </div>
              <div className="ms-3">
                <h4 className="mb-0">{stats.unreadChanges}</h4>
                <p className="text-muted mb-0">未读变更</p>
              </div>
              <Link to="/changes?isRead=false" className="ms-auto btn btn-sm btn-outline-danger">
                查看未读
              </Link>
            </div>
          </Card>
        </div>
      </div>
      
      {/* 最近变更和监控站点 */}
      <div className="row">
        {/* 最近变更 */}
        <div className="col-lg-8 mb-4 mb-lg-0">
          <Card 
            title="最近变更" 
            icon={<FaHistory className="text-primary" />}
            actions={
              <Link to="/changes" className="btn btn-sm btn-outline-primary">
                查看全部
              </Link>
            }
          >
            {stats.recentChanges?.length > 0 ? (
              <div className="list-group list-group-flush">
                {stats.recentChanges.map(change => (
                  <Link 
                    key={change._id} 
                    to={`/changes/${change._id}`} 
                    className="list-group-item list-group-item-action d-flex align-items-center py-3"
                  >
                    <div className="me-3">
                      <span 
                        className={`badge ${change.isRead ? 'bg-secondary' : 'bg-danger'}`}
                      >
                        {change.isRead ? '已读' : '未读'}
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-1">{change.site?.name} - {change.page?.title || change.page?.url}</h6>
                      <p className="text-muted mb-0 small">
                        <span 
                          className="me-2 importance-indicator d-inline-block"
                          style={{ backgroundColor: `var(--bs-${change.importance >= 4 ? 'danger' : (change.importance >= 3 ? 'warning' : 'success')})` }}
                        ></span>
                        {formatImportance(change.importance)} · {formatRelativeTime(change.createdAt)}
                      </p>
                    </div>
                    {!change.isRead && (
                      <FaExclamationTriangle className="text-warning ms-2" />
                    )}
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState 
                type="empty"
                title="暂无变更记录"
                message="您的监控站点尚无变更，请等待系统检测或添加新站点。"
              />
            )}
          </Card>
        </div>
        
        {/* 监控站点 */}
        <div className="col-lg-4">
          <Card 
            title="我的监控站点" 
            icon={<FaDesktop className="text-primary" />}
            actions={
              <Link to="/sites/new" className="btn btn-sm btn-outline-primary">
                <FaPlus className="me-1" />
                添加
              </Link>
            }
          >
            {sites.length > 0 ? (
              <div className="list-group list-group-flush">
                {sites.slice(0, 5).map(site => (
                  <Link 
                    key={site._id} 
                    to={`/sites/${site._id}`} 
                    className="list-group-item list-group-item-action py-3"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">{site.name}</h6>
                      <span className="badge bg-light text-dark">
                        {site.pages?.length || 0} 个页面
                      </span>
                    </div>
                    <small className="text-muted">
                      最后检查: {site.lastCheckedAt ? formatRelativeTime(site.lastCheckedAt) : '未检查'}
                    </small>
                  </Link>
                ))}
                
                {sites.length > 5 && (
                  <Link to="/sites" className="list-group-item list-group-item-action text-center text-primary">
                    查看全部 ({sites.length})
                  </Link>
                )}
              </div>
            ) : (
              <EmptyState 
                type="no-data"
                title="暂无监控站点"
                message="您尚未添加任何监控站点。"
                action={
                  <Link to="/sites/new" className="btn btn-primary">
                    <FaPlus className="me-2" />
                    添加站点
                  </Link>
                }
              />
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;